import { createTheme } from "@mui/material";
import {
  Color,
  primaryGreen,
  neutral,
  lightGreen,
  magenta,
  green,
  blue,
  red,
  olive,
  error,
} from "./color";
import { Grow } from "@mui/material";
// import { typography } from "./typography";

function createLightTheme(primaryColor?: Color, direction: 'ltr' | 'rtl' = 'ltr') {

  const isRTL = direction === 'rtl';
  const primaryColorVariations = [
    {
      label: "Olive Green",
      color: olive,
    },
    {
      label: "Primary Green",
      color: primaryGreen,
    },
  ];

  const theme = createTheme({

    palette: {
      contrastThreshold: 2,
      primary: {
        main: (primaryColor || primaryGreen)[800],
        light: (primaryColor || primaryGreen)[600],
        dark: (primaryColor || primaryGreen)[800],
        contrastText: neutral[100],
      },
      tonalOffset: {
        light: 0.1,
        dark: 0.9,
      },
      theme: {
        bg: "#ffffff",
        // text: "#ff0000",
        text: neutral[900],
        secondary: { ...neutral },
        primary: primaryColor || primaryGreen,
        warning: { ...olive },
        grey: { ...neutral },
        success: { ...green },
        green: { ...green },
        tertiary1: { ...olive },
        tertiary2: { ...lightGreen },
        tertiary3: { ...magenta },
        tertiary4: { ...green },
        tertiary5: { ...blue },
        tertiary6: { ...red },
        error: { ...error },
      },
      grey: {
        ...neutral,
        50: "#FFFFFF",
        A100: "#C1C1C1",   // approximate accent, lighter
        A200: "#A0A0A0",   // approximate accent
        A400: "#707070",   // approximate accent
        A700: "#4A4A4A",   // approximate accent, darker
      },
      warning: {
        main: olive[700],
        light: olive[500],
        dark: olive[700],
        contrastText: '#fff',
      },
      error: {
        main: error[600],
        light: error[300],
        dark: error[700],
        contrastText: '#fff',
      },
      success: {
        main: green[700],
        light: green[500],
        dark: green[700],
        contrastText: '#fff',
      },

    },
    typography: {
      fontFamily: `Inter, sans-serif`,
      // Text direction affects text alignment
      allVariants: {
        textAlign: isRTL ? 'right' : 'left',
      },
      h1: {
        textAlign: isRTL ? 'right' : 'left',
      },
      h2: {
        textAlign: isRTL ? 'right' : 'left',
      },
      h3: {
        textAlign: isRTL ? 'right' : 'left',
      },
      h4: {
        textAlign: isRTL ? 'right' : 'left',
      },
      h5: {
        textAlign: isRTL ? 'right' : 'left',
      },
      h6: {
        textAlign: isRTL ? 'right' : 'left',
      },
      body1: {
        textAlign: isRTL ? 'right' : 'left',
      },
      body2: {
        textAlign: isRTL ? 'right' : 'left',
      },

    },
    components: {
      // Global CSS baseline adjustments
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            direction,
            textAlign: isRTL ? 'right' : 'left',
          }, a: {
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer',
            // transition: '0.2s ease',

            // '&:hover': {
            //   textDecoration: 'underline',
            //   color: (primaryColor || primaryGreen)[800],
            // },

            // '&:active': {
            //   opacity: 0.8,
            // },
          },
        },
      },

      // AppBar RTL configuration
      MuiAppBar: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              '& .MuiToolbar-root': {
                flexDirection: 'row-reverse',
              },
            }),
          },
        },
      },

      // Toolbar RTL configuration
      MuiToolbar: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              paddingLeft: 24,
              paddingRight: 16,
              '@media (min-width: 600px)': {
                paddingLeft: 24,
                paddingRight: 24,
              },
            }),
          },
        },
      },

      // Button RTL configuration
      MuiButton: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              '& .MuiButton-startIcon': {
                marginLeft: 8,
                marginRight: -4,
                // '& > *:nth-of-type(1)': {
                //   fontSize: 20,
                // },
              },
              '& .MuiButton-endIcon': {
                marginLeft: -4,
                marginRight: 8,
                // '& > *:nth-of-type(1)': {
                //   fontSize: 20,
                // },
              },
            }),
          },
          text: {
            ...(isRTL && {
              paddingLeft: 8,
              paddingRight: 8,
            }),
          },
          outlined: {
            ...(isRTL && {
              paddingLeft: 15,
              paddingRight: 15,
            }),
            color: neutral[1000],
            backgroundColor: '#fff',
            '& svg': {
              color: 'inherit',
            }
          },
          contained: {
            ...(isRTL && {
              paddingLeft: 16,
              paddingRight: 16,
            }),
          },
          containedPrimary: {
            color: '#fff'
          },
          disabled: {
            color: '#fff'
          }
        },
      },

      // IconButton RTL configuration
      MuiIconButton: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              transform: 'scaleX(-1)',
              '& .MuiSvgIcon-root': {
                transform: 'scaleX(-1)',
              },
            }),
          },
        },
      },

      // List RTL configuration
      MuiList: {
        styleOverrides: {
          root: {
            // ...(isRTL && {
            //   paddingLeft: 0,
            //   paddingRight: 0,
            // }),
            padding: "0.25rem",
            zIndex: 700
          },
        },
      },

      // ListItem RTL configuration
      MuiListItem: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              paddingLeft: 16,
              paddingRight: 16,
              textAlign: 'right',
              flexDirection: 'row-reverse',
            }),
          },
          gutters: {
            ...(isRTL && {
              paddingLeft: 16,
              paddingRight: 16,
            }),
          },
        },
      },

      // ListItemIcon RTL configuration
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              marginRight: 0,
              marginLeft: 16,
              minWidth: 56,
              justifyContent: 'flex-end',
            }),
          },
        },
      },

      // ListItemText RTL configuration
      MuiListItemText: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
              marginLeft: 0,
              marginRight: 16,
            }),
          },
          primary: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
          secondary: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
        },
      },

      // Chip RTL configuration
      MuiChip: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              flexDirection: 'row-reverse',
            }),
          },
          icon: {
            ...(isRTL && {
              marginLeft: 5,
              marginRight: -6,
            }),
          },
          deleteIcon: {
            ...(isRTL && {
              marginLeft: -6,
              marginRight: 5,
            }),
          },
        },
      },

      // TextField RTL configuration
      MuiTextField: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              '& .MuiInputBase-root': {
                textAlign: 'right',
                direction: 'ltr'
              },
              '& .MuiInputLabel-root': {
                right: 14,
                left: 'auto',
                transformOrigin: 'top right',
              },
              '& .MuiInputLabel-shrink': {
                transformOrigin: 'top right',
                right: 14,
                left: 'auto',
              },
            }),
          },
        },
      },

      // Input RTL configuration
      MuiInput: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
        },
      },

      // OutlinedInput RTL configuration
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
              '& .MuiOutlinedInput-notchedOutline legend': {
                textAlign: 'right',
                marginRight: 5
              },
            }),

            // Target when used with Select
            '&.MuiInputBase-root': {
              '& .MuiSelect-select': {
                ...(isRTL && {
                  textAlign: 'right',
                  paddingLeft: '32px !important',
                  paddingRight: '14px',
                }),
              },
            },
          },
          input: {
            ...(isRTL && {
              textAlign: 'right',

            }),

            // Ensure proper styling when combined with Select
            '&.MuiSelect-select': {
              padding: '8.5px 14px',
              minHeight: '1.4375em',
              marginRight: !isRTL ? 0 : '',
            },
          },
        },
      },

      // FilledInput RTL configuration
      MuiFilledInput: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
          input: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
        },
      },

      // InputLabel RTL configuration
      MuiInputLabel: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              right: 14,
              left: 'auto',
              transformOrigin: 'top right',
            }),
          },
          shrink: {
            ...(isRTL && {
              transformOrigin: 'top right',
              right: 14,
              left: 'auto',
            }),
          },
        },
      },

      // FormControl RTL configuration
      MuiFormControl: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
        },
      },

      // FormLabel RTL configuration
      MuiFormLabel: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
        },
      },

      // FormHelperText RTL configuration
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
              marginLeft: 0,
              marginRight: 14,
            }),
          },
        },
      },

      // Select RTL configuration
      MuiSelect: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
          select: {
            // Target the specific select input styling
            '&.MuiSelect-select': {
              // Add your custom styles here
              padding: '8.5px 14px',
              // fontSize: '1rem',
              lineHeight: 1.4375,
              letterSpacing: '0.00938em',

              // RTL specific styles
              ...(isRTL && {
                textAlign: 'right',
                paddingLeft: '32px', // Space for dropdown icon
                paddingRight: '14px',
              }),

              // Focus styles
              '&:focus': {
                backgroundColor: 'transparent',
              },
            },

            // Target the input base and outlined input combinations
            '&.MuiInputBase-input': {
              padding: 0, // Remove default padding as it's handled by select
            },

            '&.MuiOutlinedInput-input': {
              padding: '8px 0 8px 8px',
              ...(isRTL && {
                textAlign: 'right',
              }),
            },
          },
          icon: {
            ...(isRTL && {
              left: 7,
              right: 'auto',
            }),
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: 'none',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            marginTop: '4px',
            // minWidth: '160px',
          },
          list: {
            padding: "0.25rem",
          },
        },
      },
      // MenuItem RTL configuration
      MuiMenuItem: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
              justifyContent: 'flex-end',
            }),
            padding: "0.5rem 0.75rem",
            borderRadius: "0.5rem",
            marginTop: '0.125rem',
            marginBottom: '0.125rem',
            '&:first-of-type': {
              marginTop: '0',
            },
            '&:last-of-type': {
              marginBottom: '0',
            },
            '&.Mui-selected, &.Mui-selected .MuiTypography-root': {
              color: primaryGreen[700],
              fontWeight: 500,
            },
            '&.Mui-selected, &.Mui-selected .MuiChip-root .MuiTypography-root': {
              color: 'inherit',
              fontWeight: 'inherit',
            }
          },
        },
      },

      // Checkbox RTL configuration
      MuiCheckbox: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              padding: '9px 9px 9px 9px',
            }),
          },
        },
      },

      // Radio RTL configuration
      MuiRadio: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              padding: '9px 9px 9px 9px',
            }),
          },
        },
      },

      // FormControlLabel RTL configuration
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              flexDirection: 'row',

            }),
          },
          label: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
        },
      },

      // Switch RTL configuration
      MuiSwitch: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              padding: 8,
              '& .MuiSwitch-track': {
                borderRadius: 22 / 2,
              },
              '& .MuiSwitch-thumb': {
                boxShadow: 'none',
                width: 16,
                height: 16,
                margin: 2,
              },
            }),
          },
        },
      },

      // Tab RTL configuration
      MuiTab: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              minWidth: 72,
              paddingLeft: 12,
              paddingRight: 12,
            }),
          },
        },
      },

      // Tabs RTL configuration
      MuiTabs: {
        styleOverrides: {
          root: {

            ...(isRTL && {
              flexDirection: 'row-reverse',
              // '& .MuiTabs-flexContainer': {
              //   flexDirection: 'row',
              // },
            }),
            // '& .MuiTabScrollButton-root': {
            //   display: 'none',  // Hide disabled buttons  
            // },
          },
          indicator: {
            ...(isRTL && {
              right: 'auto',
              left: 'auto',
            }),
          },
        },
      },

      // Card RTL configuration
      MuiCard: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
        },
      },

      // CardHeader RTL configuration
      MuiCardHeader: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              paddingLeft: 16,
              paddingRight: 16,
              textAlign: 'right',
            }),
          },
          content: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
          action: {
            ...(isRTL && {
              alignSelf: 'flex-start',
              marginTop: -4,
              marginRight: 'auto',
              marginLeft: -8,
            }),
          },
        },
      },

      // CardContent RTL configuration
      MuiCardContent: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              paddingLeft: 16,
              paddingRight: 16,
              textAlign: 'right',
            }),
          },
        },
      },

      // CardActions RTL configuration
      MuiCardActions: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              paddingLeft: 8,
              paddingRight: 8,
              justifyContent: 'flex-start',
            }),
          },
        },
      },

      // Dialog RTL configuration
      MuiDialog: {
        defaultProps: {
          TransitionComponent: Grow,
          transitionDuration: 350,
          BackdropProps: {
            sx: {
              backdropFilter: 'blur(3px)',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
          },
        },
        styleOverrides: {
          paper: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
        },
      },

      // DialogTitle RTL configuration
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
              paddingLeft: 24,
              paddingRight: 24,
            }),
            backgroundColor: neutral[100], borderBottom: '1px solid', borderColor: neutral[200],
            position: 'sticky', top: 0, zIndex: 1000,
            padding: '10px 24px'
          },
        },
      },

      // DialogContent RTL configuration
      MuiDialogContent: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
              paddingLeft: 24,
              paddingRight: 24,
            }),
          },
        },
      },

      // DialogActions RTL configuration
      MuiDialogActions: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              paddingLeft: 24,
              paddingRight: 24,
              justifyContent: 'flex-start',
            }),
            ...(isRTL ? {
              direction: 'ltr',
            } : {
              marginRight: 'rtl'
            }),
            backgroundColor: neutral[200],
            position: 'sticky', bottom: 0, zIndex: 1000,
            paddingLeft: 24,
            paddingRight: 24,
          },

        },
      },

      // Snackbar RTL configuration
      MuiSnackbar: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              left: 24,
              right: 'auto',
            }),
          },
        },
      },

      // SnackbarContent RTL configuration
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
              flexDirection: 'row-reverse',
            }),
          },
          action: {
            ...(isRTL && {
              paddingLeft: 0,
              paddingRight: 16,
              marginLeft: 'auto',
              marginRight: -8,
            }),
          },
        },
      },

      // Tooltip RTL configuration
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
        },
      },

      // Breadcrumbs RTL configuration
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              flexDirection: 'row-reverse',
            }),
          },
          separator: {
            ...(isRTL && {
              transform: 'scaleX(-1)',
            }),
          },
        },
      },

      // Stepper RTL configuration
      MuiStepper: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              paddingLeft: 24,
              paddingRight: 24,
            }),
          },
        },
      },

      // Step RTL configuration
      MuiStep: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
        },
      },

      // StepLabel RTL configuration
      MuiStepLabel: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              flexDirection: 'row-reverse',
            }),
          },
          label: {
            ...(isRTL && {
              textAlign: 'right',
              marginLeft: 0,
              marginRight: 8,
            }),
          },
        },
      },

      // StepContent RTL configuration
      MuiStepContent: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
              marginLeft: 0,
              marginRight: 12,
              paddingLeft: 0,
              paddingRight: 8,
              borderLeft: 'none',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            }),
          },
        },
      },

      // Accordion RTL configuration
      MuiAccordion: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              textAlign: 'right',
            }),
          },
        },
      },

      // AccordionSummary RTL configuration
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              paddingLeft: 16,
              paddingRight: 16,
              flexDirection: 'row-reverse',
            }),
          },
          content: {
            ...(isRTL && {
              textAlign: 'right',
              marginLeft: 0,
              marginRight: 16,
            }),
          },
          expandIconWrapper: {
            ...(isRTL && {
              marginLeft: 0,
              marginRight: -12,
            }),
          },
        },
      },

      // AccordionDetails RTL configuration
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              paddingLeft: 16,
              paddingRight: 16,
              textAlign: 'right',
            }),
          },
        },
      },

      // Pagination RTL configuration
      MuiPagination: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              direction: 'ltr', // Keep pagination LTR for numbers
            }),
          },
        },
      },

      // DataGrid RTL configuration - Commented out since @mui/x-data-grid is not installed
      // Uncomment this section if you install @mui/x-data-grid
      // MuiDataGrid: {
      //   styleOverrides: {
      //     root: {
      //       ...(isRTL && {
      //         '& .MuiDataGrid-cell': {
      //           textAlign: 'right',
      //         },
      //         '& .MuiDataGrid-columnHeader': {
      //           textAlign: 'right',
      //         },
      //         '& .MuiDataGrid-columnHeaderTitle': {
      //           textAlign: 'right',
      //         },
      //       }),
      //     },
      //   },
      // },

      // Avatar RTL configuration
      MuiAvatar: {
        styleOverrides: {
          root: {
            ...(isRTL && {
              marginLeft: 8,
              marginRight: 0,
            }),
          },
        },
      },

      // Badge RTL configuration
      MuiBadge: {
        styleOverrides: {
          badge: {
            ...(isRTL && {
              left: 0,
              right: 'auto',
              transform: 'scale(1) translate(-50%, -50%)',
              transformOrigin: '0% 0%',
            }),
          },
        },
      },

      MuiTableContainer: {
        styleOverrides: {
          root: {
            // 🔹 Top corners (Header)
            '& thead th:first-of-type': {
              borderTopLeftRadius: 8,
            },
            '& thead th:last-of-type': {
              borderTopRightRadius: 8,
            },

            // 🔹 Bottom corners (Footer rows)
            '& tfoot tr:last-of-type td:first-of-type': {
              borderBottomLeftRadius: 8,
            },
            '& tfoot tr:last-of-type td:last-of-type': {
              borderBottomRightRadius: 8,
            },

            // 🔹 If tfoot exists but has no td -> fallback to tbody
            '&:not(:has(tfoot td)) tbody tr:last-of-type td:first-of-type': {
              borderBottomLeftRadius: 8,
            },
            '&:not(:has(tfoot td)) tbody tr:last-of-type td:last-of-type': {
              borderBottomRightRadius: 8,
            },

            // 🔹 If tfoot doesn't exist at all -> fallback to tbody
            '&:not(:has(tfoot)) tbody tr:last-of-type td:first-of-type': {
              borderBottomLeftRadius: 8,
            },
            '&:not(:has(tfoot)) tbody tr:last-of-type td:last-of-type': {
              borderBottomRightRadius: 8,
            },

            // 🔹 Optional: Prevent content bleeding on rounded edges
            '& tbody tr:last-of-type td, & tfoot tr:last-of-type td': {
              overflow: 'hidden',
            },
            'td:has(.no-data)': {
              border: `1px solid ${neutral[200]}`,
              borderTop: 0
              // borderRadius: 0 0 8px 8px;
            },
            '& .MuiTableRow-root:hover:last-of-type': {
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            },
            '& .MuiTableCell-root:first-child': {
              borderLeft: '1px solid #efefef'
            },
            '&:not(:has(tfoot td)) tbody tr:last-of-type .MuiTableCell-roote': {
              borderBottomRightRadius: 8,
            },

            '& tfoot tr:last-of-type .MuiTableCell-root': {
              borderBottom: '1px solid #efefef'
            },
            '&:not(:has(tfoot td)) tbody tr:last-of-type .MuiTableCell-root': {
              borderBottom: '1px solid #efefef'
            },
            // 🔹 If tfoot doesn't exist at all -> fallback to tbody
            '&:not(:has(tfoot)) tbody tr:last-of-type .MuiTableCell-root': {
              borderBottom: '1px solid #efefef'
            },
            '& .MuiTableRow-head': {
              boxShadow: 'none'
            }
          },
        },
      }

    },
  });

  return { theme, primaryColorVariations };
}

function createDarkTheme(primaryColor?: Color) {
  const primaryColorVariations = [
    {
      label: "Red",
      color: red,
    },
    {
      label: "Blue",
      color: blue,
    },
  ];

  const theme = createTheme({
    palette: {
      theme: {
        bg: "#d9def2",
        text: "#002fee",
        primary: {
          ...(primaryColor ? primaryColor : blue),
        },
        secondary: {
          ...neutral,
        },
        tertiary1: { ...olive },
        tertiary2: { ...lightGreen },
        tertiary3: { ...magenta },
        tertiary4: { ...green },
        tertiary5: { ...blue },
        tertiary6: { ...red },
        error: { ...error },
      },
    },
  });

  return { theme, primaryColorVariations };
}

export const themes = { createLightTheme, createDarkTheme };

// Export individual theme functions
export { createLightTheme, createDarkTheme };

// Export color system
export * from './color';

// Export theme types
// export * from './types/extendMUIPalette'; // Temporarily disabled - .d.ts files can't be imported as modules

// Export theme components
export { default as ThemeSelector } from './themeSelector';
export { default as ThemeWrapper } from './themeWrapper';